"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _classnames = _interopRequireDefault(require("classnames"));
var _label = _interopRequireDefault(require("@ux/label"));
var _text = _interopRequireDefault(require("@ux/text"));
const _excluded = ["children", "className", "divider", "gap", "id", "invalid", "label", "helpMessage", "errorMessage", "required", "orientation", "stretch", "wrap"];
/* eslint-disable complexity */
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const UX_SIBLINGSET_CLASSNAME = 'ux-sibling-set';

/**
 * Creates a SiblingSet component
 *
 * @param {Object} props - Properties to inform the component
 * @param {Elements} props.children - Given children as siblings
 * @param {String} [props.className] - Optional class name
 * @param {Boolean} [props.divider] - Optional divider
 * @param {String} [props.gap] - Optional amount of space between siblings, sm md lg
 * @param {String} [props.id] - Used to inform a11y internals
 * @param {Boolean|String} [props.invalid] - Sets invalid within the component
 * @param {Object} [props.label] - Label configuration object
 * @param {Element} [props.label.content] - Content for the label
 * @param {String} [props.label.id] - `id` for the label
 * @param {String} [props.helpMessage] - Help message
 * @param {String} [props.errorMessage] - Error message
 * @param {String} [props.orientation] - Directions for layout, horizontal or vertical
 * @param {Boolean} [props.stretch] - Determines if the siblings should stretch the full width of the container
 * @param {Boolean} [props.wrap] - Wrap children to the next line
 * @returns {Component} - The SiblingSet component
 */
const SiblingSet = /*#__PURE__*/_react.default.forwardRef(function SiblingSet(_ref, ref) {
  let {
      children,
      className,
      divider,
      gap,
      id,
      invalid,
      label,
      helpMessage,
      errorMessage,
      required,
      orientation = 'horizontal',
      stretch,
      wrap
    } = _ref,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  delete rest.style;
  if (!children) {
    return null;
  }
  const ariaLabelledBy = label && id && `${id}-label`;
  const helpMessageId = helpMessage && id && `${id}-help`;
  const errorMessageId = errorMessage && id && `${id}-error`;
  return /*#__PURE__*/_react.default.createElement("div", {
    "data-version": "2400.4",
    className: (0, _classnames.default)(UX_SIBLINGSET_CLASSNAME, className, `${UX_SIBLINGSET_CLASSNAME}-${orientation}`, {
      [`${UX_SIBLINGSET_CLASSNAME}-gap-${gap}`]: gap,
      [`${UX_SIBLINGSET_CLASSNAME}--stretch`]: stretch,
      [`${UX_SIBLINGSET_CLASSNAME}--wrap`]: wrap
    }),
    role: "group",
    "aria-labelledby": ariaLabelledBy
  }, ariaLabelledBy && /*#__PURE__*/_react.default.createElement(_label.default, {
    className: `${UX_SIBLINGSET_CLASSNAME}-label`,
    id: ariaLabelledBy,
    htmlFor: id,
    required: required
  }, label), divider && /*#__PURE__*/_react.default.createElement("hr", null), /*#__PURE__*/_react.default.createElement(_text.default.div, (0, _extends2.default)({
    id: id,
    ref: ref,
    style: {
      '--ux-siblingset-amount': children.length
    },
    className: `${UX_SIBLINGSET_CLASSNAME}-children`,
    emphasis: invalid ? 'critical' : null
  }, rest), _react.Children.map(children, (child, i) => {
    if (!child) return null;
    const childProps = child === null || child === void 0 ? void 0 : child.props;
    return /*#__PURE__*/(0, _react.cloneElement)(child, _objectSpread(_objectSpread({}, childProps), {}, {
      key: `${id}-child${i}`,
      ['aria-invalid']: invalid,
      ['aria-errormessage']: errorMessageId,
      ['aria-describedby']: id ? [helpMessageId, childProps['aria-describedby']].filter(Boolean).join(' ') : null
    }));
  })), helpMessage && /*#__PURE__*/_react.default.createElement(_text.default.p, {
    text: helpMessage,
    as: "caption",
    id: helpMessageId,
    emphasis: "passive"
  }), errorMessage && /*#__PURE__*/_react.default.createElement(_text.default.p, {
    text: errorMessage,
    as: "caption",
    id: errorMessageId,
    emphasis: "critical",
    role: "alert"
  }));
});

/**
 * Determine if given collection of props are valid
 *
 * @param {Object} props - List of given props
 * @param {String} propName - Name of current prop
 * @param {String} componentName - Name of the component
 * @returns {PropTypeValidation} - Property configuration validity
 */
function oneOfRequiredProps(props, propName, componentName) {
  const labelNoId = props.label && !props.id;
  const helpNoId = props.helpMessage && !props.id;
  const errorNoId = props.errorMessage && !props.id;
  if (labelNoId || helpNoId || errorNoId) {
    return new Error(`When using a label, helpMessage or errorMessage, the 'id' prop is required`);
  }
  return _propTypes.default.checkPropTypes({
    [propName]: _propTypes.default.node
  }, props, propName, componentName);
}
SiblingSet.propTypes = {
  /** Children to render as siblings */
  children: _propTypes.default.node,
  /** Optional class name used to help position the component */
  className: _propTypes.default.string,
  /** Sets a divider between the label and siblings, helpful for segregating the label from the siblings */
  divider: _propTypes.default.bool,
  /** Gap between the siblings, no gap by default */
  gap: _propTypes.default.oneOf(['sm', 'md', 'lg']),
  /** Used to inform a11y internals */
  id: oneOfRequiredProps,
  /** Sets the component as invalid */
  invalid: _propTypes.default.oneOfType([_propTypes.default.bool, _propTypes.default.oneOf(['grammar', 'spelling'])]),
  /** Optional label for the siblings */
  label: oneOfRequiredProps,
  /** Optional message for the siblings */
  helpMessage: oneOfRequiredProps,
  /** Optional error message for the siblings */
  errorMessage: oneOfRequiredProps,
  /** The direction of the siblings, either horizontal or vertical */
  orientation: _propTypes.default.oneOf(['horizontal', 'vertical']),
  /** Optionally shows group as required */
  required: _label.default.propTypes.required,
  /** Stretch the width of the container, helps for larger siblings */
  stretch: _propTypes.default.bool,
  /** Wrap children when appropriate */
  wrap: _propTypes.default.bool
};
SiblingSet.displayName = 'SiblingSet';
var _default = exports.default = SiblingSet;